import axiosInstance from "../../utils/axiosInstance";

// notification
import { notify } from "../../components/alerts";

import {
  setDocument,
  setDocuments,
  setSharedDocuments,
  removeDocument,
  setLoading,
  updateDocument,
} from "../../store/slices/document";
import { AppDispatch } from "../../store/store";
import {
  DocumentId,
  IDocument,
  IUploadDocument,
} from "../../store/@types/document";
import { isAxiosError } from "axios";
import { NavigateFunction } from "react-router-dom";

const documentEndpoint = "/api/v1/client/companies";

export const get_documents = async (
  dispatch: AppDispatch,
  limit: number = 20,
  offset: number = 0,
) => {
  dispatch(setLoading(true));
  try {
    const params = new URLSearchParams({
      limit: String(limit),
      offset: String(offset),
    });
    const response = await axiosInstance.get(
      `${documentEndpoint}/documents?${params.toString()}`,
    );
    const { data } = response.data;
    dispatch(setDocuments(data));
  } catch (err) {
    if (isAxiosError(err)) {
      notify({
        type: "error",
        message: err.response?.data?.message || "Failed to load documents",
      });
    } else {
      throw err;
    }
  } finally {
    dispatch(setLoading(false));
  }
};

export const getSharedDocuments = async (
  dispatch: AppDispatch,
  current_page: number = 1,
) => {
  dispatch(setLoading(true));
  try {
    const response = await axiosInstance.get(
      `/shared-documents?page=${current_page}`,
    );

    dispatch(setSharedDocuments(response.data));
  } catch (err) {
    // logger action
  }
  dispatch(setLoading(false));
};

export const get_document = async (dispatch: AppDispatch, id: DocumentId) => {
  dispatch(setLoading(true));
  try {
    const response = await axiosInstance.get(
      `${documentEndpoint}/documents/${id}`,
    );
    dispatch(setDocument(response.data.data));
    return response.data.data as IDocument;
  } catch (err) {
    notify({
      type: "error",
      message: isAxiosError(err)
        ? err.response?.data?.message || "Failed to load document."
        : "Failed to load document.",
    });
    return undefined;
  } finally {
    dispatch(setLoading(false));
  }
};

export const shareDocument = async (dispatch: AppDispatch, formData: any) => {
  let result = false;
  dispatch(setLoading(true));
  try {
    await axiosInstance.post("/share-document", formData);

    notify({ type: "success", message: `File Shared with ${formData.email}.` });
    result = true;
  } catch (err) {
    result = false;
    notify({
      type: "error",
      message: isAxiosError(err)
        ? err.response?.data?.message || "Please enter a valid recipient email."
        : "The document could not be shared.",
    });
  } finally {
    dispatch(setLoading(false));
  }

  return result;
};

export const renew_document = async (
  dispatch: AppDispatch,
  data: IUploadDocument,
) => {
  dispatch(setLoading(true));
  try {
    const response = await axiosInstance.post("/renew-document", data);

    notify({
      type: "success",
      message: response.data.message,
    });
  } catch (err: any) {
    if (isAxiosError(err))
      notify({
        type: "error",
        message: "This order cannot be placed.",
      });
  }
  dispatch(setLoading(false));
};

export const create_document = async (
  dispatch: AppDispatch,
  form: IUploadDocument,
  navigate: NavigateFunction,
) => {
  dispatch(setLoading(true));
  try {
    const response = await axiosInstance.post(
      `${documentEndpoint}/documents`,
      form,
    );
    const { message } = response.data;
    notify({ type: "success", message });
    navigate(-1);
  } catch (err) {
    if (isAxiosError(err)) {
      notify({
        type: "error",
        message:
          err.response?.data?.message || "The document could not be created.",
      });
    } else {
      throw err;
    }
  } finally {
    dispatch(setLoading(false));
  }
};

export const update_document = async (
  dispatch: AppDispatch,
  id: DocumentId,
  form: IUploadDocument,
  navigate: NavigateFunction,
) => {
  dispatch(setLoading(true));
  try {
    const response = await axiosInstance.patch(
      `${documentEndpoint}/documents/${id}`,
      form,
    );
    const { message } = response.data;
    notify({ type: "success", message });
    dispatch(updateDocument({ id, ...form }));
    if (response.data.data) dispatch(setDocument(response.data.data));
    navigate(-1);
    return true;
  } catch (err) {
    if (isAxiosError(err)) {
      notify({
        type: "error",
        message:
          err.response?.data?.message || "This document cannot be updated.",
      });
    } else {
      throw err;
    }
    return false;
  } finally {
    dispatch(setLoading(false));
  }
};

export const deleteDocument = async (
  dispatch: AppDispatch,
  id: DocumentId,
  navigate?: NavigateFunction,
) => {
  dispatch(setLoading(true));
  try {
    const response = await axiosInstance.delete(
      `${documentEndpoint}/documents/${id}`,
    );

    notify({
      type: "success",
      message: response.data.message,
    });
    dispatch(removeDocument(id));
    navigate?.("/dashboard/documents");
    return true;
  } catch (err) {
    notify({
      type: "error",
      message: isAxiosError(err)
        ? err.response?.data?.message || "This file cannot be deleted."
        : "This file cannot be deleted.",
    });
    if (!isAxiosError(err)) throw err;
    return false;
  } finally {
    dispatch(setLoading(false));
  }
};

export const delete_document = deleteDocument;
